import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrandButton } from "@/components/PageHeader";
import { api, cedi2 as cedi, errMsg } from "@/lib/api";
import { ChequeFields, EMPTY_CHEQUE, chequeOk, postCheque, available } from "@/components/payments/ChequeDialogs";

export const PAY_METHODS = ["Cash", "MoMo", "Card", "Bank", "Cheque"];

const Form = ({ t, onDone }) => {
  const balance = available(t);
  const [amount, setAmount] = useState(balance.toFixed(2));
  const [method, setMethod] = useState("Cash");
  const [cheque, setCheque] = useState({ ...EMPTY_CHEQUE, issuer: t.delegate_name });
  const [busy, setBusy] = useState(false);
  const value = Number(amount);
  const isCheque = method === "Cheque";
  const bad = !(value > 0) || value > balance || (isCheque && !chequeOk(cheque));
  const save = async () => {
    setBusy(true);
    try {
      if (isCheque) {
        const c = await postCheque(cheque, [{ txn_id: t.id, amount: value }]);
        toast.success(`Cheque recorded · receipt ${c.receipt_no}. Mark it cleared when the bank confirms.`);
        onDone(null, c);
      } else {
        const r = await api.post(`/transactions/${t.id}/payments`, { amount: value, method });
        toast.success(`${cedi(value)} recorded. Email and SMS alert queued for ${t.delegate_name}.`);
        onDone(r.data);
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-4 text-sm">
      <div className="rounded-md bg-[#F8F9FA] p-3 grid grid-cols-3 gap-2 text-center" data-testid="pay-dialog-summary">
        <div><div className="text-[11px] text-gray-500">Total</div><div className="font-mono font-semibold">{cedi(t.amount)}</div></div>
        <div><div className="text-[11px] text-gray-500">Paid</div><div className="font-mono font-semibold">{cedi(t.paid || 0)}</div></div>
        <div><div className="text-[11px] text-gray-500">Balance</div><div className="font-mono font-semibold text-[#E60000]">{cedi(balance)}</div></div>
      </div>
      {(t.pending_cheque || 0) > 0 && <p className="text-xs text-amber-800" data-testid="pay-dialog-pending-cheque">{cedi(t.pending_cheque)} is covered by a cheque waiting to clear.</p>}
      <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-gray-600">Amount received (GH₵)</span>
        <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 h-10 font-mono" data-testid="pay-dialog-amount" />
      </label>
      {value > balance && <p className="text-xs text-[#E60000]" data-testid="pay-dialog-over">Amount is more than the balance.</p>}
      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Paid by</span>
        <div className="grid grid-cols-5 gap-2 mt-1.5">
          {PAY_METHODS.map((m) => (
            <button key={m} type="button" onClick={() => setMethod(m)} data-testid={`pay-dialog-method-${m.toLowerCase()}`}
              className={`h-10 rounded-md border text-sm font-semibold transition-colors ${method === m ? "border-[#E60000] bg-[#FFF0F0]" : "border-[#E5E7EB] hover:border-gray-400"}`}>{m}</button>
          ))}
        </div>
      </div>
      {isCheque && <ChequeFields c={cheque} set={setCheque} />}
      <BrandButton className="w-full h-11" disabled={busy || bad} onClick={save} data-testid="pay-dialog-submit">{busy ? "Recording…" : `Record ${cedi(value || 0)}`}</BrandButton>
    </div>
  );
};

const RefundForm = ({ t, onDone }) => {
  const [method, setMethod] = useState("Cash");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/transactions/${t.id}/refund`, { method, reason });
      toast.success(`${cedi(t.paid)} refunded to ${t.delegate_name}. Email and SMS alert queued.`);
      onDone(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-4 text-sm">
      <p className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs" data-testid="refund-dialog-note">This refunds the full {cedi(t.paid)} paid. After the refund, an administrator can delete the registration.</p>
      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Refunded by</span>
        <div className="grid grid-cols-5 gap-2 mt-1.5">
          {PAY_METHODS.map((m) => (
            <button key={m} type="button" onClick={() => setMethod(m)} data-testid={`refund-dialog-method-${m.toLowerCase()}`}
              className={`h-10 rounded-md border text-sm font-semibold transition-colors ${method === m ? "border-[#E60000] bg-[#FFF0F0]" : "border-[#E5E7EB] hover:border-gray-400"}`}>{m}</button>
          ))}
        </div>
      </div>
      <label className="block"><span className="text-xs font-bold uppercase tracking-wider text-gray-600">Reason</span>
        <Input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Participant cannot attend" className="mt-1.5 h-10" data-testid="refund-dialog-reason" />
      </label>
      <BrandButton className="w-full h-11" disabled={busy || reason.trim().length < 3} onClick={save} data-testid="refund-dialog-submit">{busy ? "Refunding…" : `Refund ${cedi(t.paid)}`}</BrandButton>
    </div>
  );
};

export const RefundDialog = ({ t, onClose, onDone }) => (
  <Dialog open={!!t} onOpenChange={onClose}>
    <DialogContent className="max-w-md" data-testid="refund-dialog">
      <DialogHeader><DialogTitle className="font-heading">Refund · {t?.delegate_name}</DialogTitle></DialogHeader>
      {t && <p className="text-xs text-gray-500 -mt-2">{t.event_title} · Ticket {t.ticket_no}</p>}
      {t && <RefundForm t={t} onDone={onDone} />}
    </DialogContent>
  </Dialog>
);

export const RecordPaymentDialog = ({ t, onClose, onDone }) => (
  <Dialog open={!!t} onOpenChange={onClose}>
    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="pay-dialog">
      <DialogHeader><DialogTitle className="font-heading">Record payment · {t?.delegate_name}</DialogTitle></DialogHeader>
      {t && <p className="text-xs text-gray-500 -mt-2">{t.event_title} · {t.category_name}</p>}
      {t && <Form t={t} onDone={onDone} />}
    </DialogContent>
  </Dialog>
);
